import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FixedVariableExpense } from '@common/models';
import { AnalyticsService, CommerceService, FixedVariableExpensesService, ToastService, UtilityService } from '@common/services';
import { DEFAULT } from '@constants/default';
import { trackEvent } from '@intercom/messenger-js-sdk';
import { UserService } from '@modules/auth/services';
import { PaymentMethod } from '@modules/commerce/models';
import { ModalDismissReasons, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { firstValueFrom, map, Observable, shareReplay, Subscription, take } from 'rxjs';

const initialDataForm: FixedVariableExpense = {
    _id: '',
    name: '',
    amount: 0,
    category: '',
    type: 'variable',
    payDays: '',
    lastPaymentDay: null,
    paymentDate: null,
    registryDate: null,
    paymentType: '',
    selectedBankAccount: [],
    paymentFrequency: 'mensual',
    exchangeRate: 0,
    description: '',
    amountVES: Number(0),
};

interface BankAccountData {
    selectedBankAccount: string;
    selectedPM: string;
    currency: string;
    exchangeRate: number;
    bankAccountVESPayment: number;
    bankAccountUSDPayment: number;
}

@Component({
    selector: 'fina-form-fixed-variable-expenses',
    templateUrl: './form-fixed-variable-expenses.component.html',
    styleUrls: ['form-fixed-variable-expenses.component.scss'],
})
export class FormFixedVariableExpensesComponent implements OnInit, OnDestroy {
    @Input() dataToUpdate: FixedVariableExpense = null;
    @Input() location: string = '';
    @Input() compactMode: boolean = false;
    currentDate: { year: number; month: number; day: number };
    minDate: { year: number; month: number; day: number };
    categories: string[] = DEFAULT.expensesCategories;
    hasDeletePermission: Observable<boolean>;
    hasCreatePermission: Observable<boolean>;
    hasUpdatePermission: Observable<boolean>;
    loading: boolean = false;

    expenseForm: FormGroup = this.fb.group({
        _id: [''],
        amount: [0, [Validators.required, Validators.min(0.01)]], // amount in USD
        amountVES: [0, [Validators.required, Validators.min(0.01)]], // amount in VES
        bankAccountData: this.fb.array([]),
        category: ['', [Validators.required]],
        description: [''],
        exchangeRate: [''],
        lastPaymentDay: [null],
        name: ['', [Validators.required]],
        payDays: [''],
        paymentDate: [null],
        paymentFrequency: ['mensual'],
        paymentType: [''],
        registryDate: [null],
        selectedBankAccount: this.fb.array([]),
        type: [''], // fixed or variable
    });

    get selectedBankAccount(): FormArray {
        return this.expenseForm.get('selectedBankAccount') as FormArray;
    }

    get bankAccountData(): FormArray {
        return this.expenseForm.get('bankAccountData') as FormArray;
    }

    private subscriptions: Subscription[] = [];

    constructor(
        private fb: FormBuilder,
        public analyticsService: AnalyticsService,
        private commerceService: CommerceService,
        public fveService: FixedVariableExpensesService,
        private modalService: NgbModal,
        private toastService: ToastService,
        public userService: UserService,
        private utilityService: UtilityService,
    ) {}

    ngOnInit() {
        this.getExchangeRate();
        this.currentDate = this.getCurrentDate();
        this.getMinDate();

        this.resetForm();

        if (this.dataToUpdate) {
            this.completeFormToUpdate();
            this.expenseForm.get('type')?.disable();
        } else {
            this.expenseForm.get('type')?.enable();
            this.expenseForm.get('registryDate')?.setValue(this.getCurrentDate());
        }

        this.hasDeletePermission = this.userService.user$.pipe(
            map(user => user.permissions?.FIXED_AND_VARIABLE_EXPENSES.includes('delete-fixed-variable-expenses')),
            shareReplay(1),
        );

        this.hasCreatePermission = this.userService.user$.pipe(
            map(user => user.permissions?.FIXED_AND_VARIABLE_EXPENSES.includes('create-fixes-variables-expenses')),
            shareReplay(1),
        );

        this.hasUpdatePermission = this.userService.user$.pipe(
            map(user => user.permissions?.FIXED_AND_VARIABLE_EXPENSES.includes('edit-fixed-variable-expenses')),
            shareReplay(1),
        );
    }

    ngOnDestroy() {
        // Unsubscribe from all subscriptions to prevent memory leaks
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    isInvalidField(form: FormGroup, fieldName: string): boolean {
        const control = form.get(fieldName);
        return control ? control.errors !== null && control.touched && control.invalid : false;
    }

    isValidField(form: FormGroup, fieldName: string): boolean {
        const control = form.get(fieldName);
        return control ? control.valid && control.touched : false;
    }

    completeFormToUpdate() {
        const { amount, exchangeRate } = this.dataToUpdate;
        const { exchangeRate: exchangeRateForm } = this.expenseForm.getRawValue();
        const amountVES = Number(amount || 0) * Number(exchangeRate || exchangeRateForm || 0);

        this.resetForm({ ...this.dataToUpdate, amountVES });

        if (this.dataToUpdate.paymentDate) {
            const date = this.dataToUpdate.paymentDate;
            this.expenseForm.get('paymentDate')?.setValue({
                year: Number(moment(date.toString()).format('YYYY')),
                month: Number(moment(date.toString()).format('M')),
                day: Number(moment(date.toString()).format('D')),
            });
        }
    }

    resetForm(value: Partial<FixedVariableExpense> = {}) {
        const { exchangeRate } = this.expenseForm.getRawValue();
        this.bankAccountData.clear();
        this.selectedBankAccount.clear();
        this.expenseForm.reset({ ...initialDataForm, exchangeRate, ...value });
    }

    onChangeType(type: 'fixed' | 'variable') {
        const { amount, amountVES, category, exchangeRate, name } = this.expenseForm.getRawValue();
        this.resetForm({ amount, amountVES, category, exchangeRate, name });
        this.expenseForm.get('type')?.setValue(type);

        if (type === 'variable') this.onAddBankAccount();
    }

    save() {
        if (this.dataToUpdate) {
            this.onUpdateFVE();
            trackEvent('update_expenses');
        } else {
            this.onAddFVE();
            trackEvent('add_expenses');
        }
    }

    delete() {
        this.loading = true;
        this.fveService.deleteFixedVariableExpense$(this.dataToUpdate._id).subscribe({
            next: (res: any) => {
                this.setAfterSuccess(res);
            },
            error: (err: any) => {
                this.setAfterError(err);
            },
        });
    }

    onUpdateFVE() {
        this.loading = true;

        const { bankAccountData, amountVES, exchangeRate, ...expenseForm } = this.expenseForm.getRawValue();
        const formData = { ...this.dataToUpdate, ...expenseForm };
        this.fveService.updateFixedVariableExpense$(this.dataToUpdate._id, formData, bankAccountData).subscribe({
            next: (res: any) => {
                this.setAfterSuccess(res);
            },
            error: (err: any) => {
                this.setAfterError(err);
            },
        });
    }

    onAddFVE() {
        this.loading = true;

        const { bankAccountData, ...formData } = this.expenseForm.getRawValue();
        this.fveService.createFixedVariableExpense$(formData, bankAccountData).subscribe({
            next: (res: any) => {
                this.setAfterSuccess(res);
            },
            error: (err: any) => {
                this.setAfterError(err);
            },
        });
    }

    onAddBankAccount() {
        const exchangeRate = this.utilityService.toFixed(this.expenseForm.get('exchangeRate')?.value, 2);

        const bankIds: FormControl = new FormControl('', [Validators.required]);
        this.selectedBankAccount.push(bankIds);

        const amountUSD = this.bankAccountData.value.length === 0 ? this.utilityService.toFixed(this.expenseForm.get('amount')?.value, 2) : 0;
        const amountVES = this.bankAccountData.value.length === 0 ? this.utilityService.toFixed(amountUSD * exchangeRate, 2) : 0;

        const bank = this.fb.group({
            selectedPM: ['', [Validators.required]],
            selectedBankAccount: ['', [Validators.required]],
            currency: ['', [Validators.required]],
            exchangeRate: [exchangeRate, [Validators.required, Validators.min(0.01)]],
            bankAccountVESPayment: [amountVES, [Validators.required, Validators.min(0.01)]],
            bankAccountUSDPayment: [amountUSD, [Validators.required, Validators.min(0.01)]],
        });
        this.bankAccountData.push(bank);
    }

    onRemoveBankAccount(i: number) {
        this.bankAccountData.removeAt(i);
        this.selectedBankAccount.removeAt(i);

        this.onChangeBankAccountAmount({ index: 0, currency: 'USD' });
    }

    // SOP-0061: la cuenta bancaria y la moneda se derivan del método de pago seleccionado.
    // Se resuelve contra el mismo subject que alimenta las opciones del select; un método sin
    // cuenta vinculada (o aún no cargado) limpia la línea para no dejar una selección a medias.
    onChangePM(i: number, pmId: string) {
        const selectedPM: any = this.fveService.paymentMethods$.getValue().find((pm: PaymentMethod) => pm._id === pmId);

        if (!selectedPM?.bankAccount?._id) {
            this.bankAccountData.at(i).get('selectedPM')?.setValue('');
            this.bankAccountData.at(i).get('selectedBankAccount')?.setValue('');
            this.bankAccountData.at(i).get('currency')?.setValue('');
            this.selectedBankAccount.at(i).setValue('');
            return;
        }

        this.bankAccountData.at(i).get('selectedPM')?.setValue(selectedPM._id);
        this.bankAccountData.at(i).get('selectedBankAccount')?.setValue(selectedPM.bankAccount._id);
        this.bankAccountData
            .at(i)
            .get('currency')
            ?.setValue((selectedPM.bankAccount.currency || '').toString());

        this.selectedBankAccount.at(i).setValue(selectedPM.bankAccount._id);
    }

    onChangeBankAccountAmount(data: { index: number; currency: 'USD' | 'VES' }) {
        const { index, currency } = data;
        const exchangeRate = this.utilityService.toFixed(this.bankAccountData.at(index).get('exchangeRate')?.value, 2);
        const amountVES = this.utilityService.toFixed(this.bankAccountData.at(index).get('bankAccountVESPayment')?.value, 2);
        const amountUSD = this.utilityService.toFixed(this.bankAccountData.at(index).get('bankAccountUSDPayment')?.value, 2);

        this.bankAccountData.at(index).get('exchangeRate')?.setValue(exchangeRate);

        if (currency === 'USD') {
            const newAmountUSD = this.utilityService.toFixed(amountVES / exchangeRate, 2);
            this.bankAccountData.at(index).get('bankAccountUSDPayment')?.setValue(newAmountUSD);
            this.bankAccountData.at(index).get('bankAccountVESPayment')?.setValue(amountVES);
        } else {
            const newAmountVES = this.utilityService.toFixed(amountUSD * exchangeRate, 2);
            this.bankAccountData.at(index).get('bankAccountUSDPayment')?.setValue(amountUSD);
            this.bankAccountData.at(index).get('bankAccountVESPayment')?.setValue(newAmountVES);
        }

        const totalAmountUSD = this.utilityService.toFixed(
            this.bankAccountData.value.reduce((acc: number, baData: BankAccountData) => acc + Number(baData.bankAccountUSDPayment), 0),
            2,
        );
        const totalAmountVES = this.utilityService.toFixed(totalAmountUSD * exchangeRate, 2);

        this.expenseForm.get('amount')?.setValue(totalAmountUSD);
        this.expenseForm.get('amountVES')?.setValue(totalAmountVES);
    }

    onChangeAmount(currency: 'USD' | 'VES') {
        const exchangeRate = this.utilityService.toFixed(this.expenseForm.get('exchangeRate')?.value, 2) || 0;
        const amountUSD = this.utilityService.toFixed(this.expenseForm.get('amount')?.value, 2) || 0;
        const amountVES = this.utilityService.toFixed(this.expenseForm.get('amountVES')?.value, 2) || 0;

        this.expenseForm.get('exchangeRate')?.setValue(exchangeRate);

        if (currency === 'USD') {
            const newAmountUSD = this.utilityService.toFixed(amountVES / exchangeRate, 2);
            this.expenseForm.get('amount')?.setValue(newAmountUSD);
            this.expenseForm.get('amountVES')?.setValue(amountVES);
        }
        if (currency === 'VES') {
            const newAmountVES = this.utilityService.toFixed(amountUSD * exchangeRate, 2);
            this.expenseForm.get('amount')?.setValue(amountUSD);
            this.expenseForm.get('amountVES')?.setValue(newAmountVES);
        }
    }

    rateLabel(value: number): string {
        return this.commerceService.labelForRate(Number(value));
    }

    delay(milliseconds: number) {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }

    open(content: TemplateRef<any>, modalOptions: NgbModalOptions = {}) {
        // SOP-0061: los métodos se cargan al abrir el modal (no en ngOnInit) — este componente se
        // instancia una vez por fila de la tabla de gastos y dispararía un GET por fila.
        this.getPaymentMethods();
        this.modalService.open(content, modalOptions);

        if (!this.dataToUpdate) {
            this.resetForm();
            this.onChangeType('variable');
        }
    }

    _getDismissReason(reason: unknown): string {
        this.resetForm();

        if (reason === ModalDismissReasons.ESC) {
            return 'by pressing ESC';
        } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
            return 'by clicking on a backdrop';
        } else {
            return `with: ${reason}`;
        }
    }

    // SOP-0061: carga los métodos de pago del selector y los publica en el subject del servicio
    // (el form puede abrirse fuera del contenedor de gastos, que es quien los cargaba)
    private getPaymentMethods() {
        firstValueFrom(this.fveService.getPaymentMethods$())
            .then((res: any) => {
                this.fveService.paymentMethods = [...res.data];
            })
            .catch(err => {
                this.toastService.error(err.error.title, err.error.message);
            });
    }

    private getCurrentDate(): { year: number; month: number; day: number } {
        const dateObject = new Date();
        const currentDate = {
            year: dateObject.getFullYear(),
            month: dateObject.getMonth() + 1,
            day: dateObject.getDate(),
        };
        return currentDate;
    }

    private getMinDate(): void {
        const subscription = this.commerceService._commerce$.subscribe({
            next: (res: any) => {
                if (res !== null && res !== undefined) {
                    const dateObject = new Date(res.createdAt);
                    const minDate = {
                        year: dateObject.getFullYear(),
                        month: dateObject.getMonth() + 1,
                        day: dateObject.getDate(),
                    };
                    this.minDate = { ...minDate };
                }
            },
        });
        this.subscriptions.push(subscription);
    }

    private getExchangeRate() {
        const subscription = this.commerceService.exchangeRateInUse$.subscribe({
            next: (value: number) => {
                this.expenseForm.get('exchangeRate')?.setValue(value);
            },
        });
        this.subscriptions.push(subscription);
    }

    private setAfterSuccess(res: { title: string; message: string }): void {
        this.fveService.getFixedVariableExpenses().pipe(take(1)).subscribe();
        this.toastService.success(res.title, res.message);
        console.log('Toast service called with:', res.title, res.message);
        console.log('Toasts in service after adding:', this.toastService.toasts);
        (async () => {
            await this.delay(240);
            this.loading = false;
            this.modalService.dismissAll(true);
            this.resetForm();
        })();
    }

    private setAfterError(err: { error: { title: string; message: string } }): void {
        this.loading = false;
        this.toastService.error(err.error.title, err.error.message);
    }
}
